import { isChief, type ActorDTO, type MessageDTO, type TaskDTO, type ThreadDTO } from "@roomate/core";

/** Spec §5.2: never a bare name for an agent ("Chief (agent)" for the default name). */
export const chiefLabel = (actor: Pick<ActorDTO, "name"> | null | undefined): string => `${actor?.name || "Chief"} (agent)`;
/** Id of the placeholder thread shown (pinned) before the first message exists. */
export const NEW_CHIEF_THREAD = "chief:new";

export type ChiefBannerState = "offline" | "paused" | "capped";

/** The active Chief actor, or null when none is enabled. */
export function chiefActor(actors: readonly ActorDTO[]): ActorDTO | null {
  return actors.find((a) => isChief(a) && a.active) ?? null;
}

/** A task-less thread between exactly the person and the Chief: the only kind the Chief answers in (spec §5.2). */
export const isChiefThread = (thread: ThreadDTO, meId: string, chiefId: string): boolean =>
  thread.taskId === null && thread.participants.length === 2 && thread.participants.includes(meId) && thread.participants.includes(chiefId);

/**
 * The person's one Chief thread in a project (spec §12.1.1), newest first. Roomie's hand-off and the pinned
 * Conversations thread both use this, so they always agree on which thread is "the" Chief thread.
 */
export function pickChiefThread(threads: readonly ThreadDTO[], projectId: string, meId: string, chiefId: string): ThreadDTO | null {
  const mine = threads.filter((t) => t.projectId === projectId && isChiefThread(t, meId, chiefId));
  return [...mine].sort((a, b) => b.lastMessageAt - a.lastMessageAt)[0] ?? null;
}

/**
 * Which thread the page shows. Until the list has loaded nothing is chosen, so a cold load never stores the
 * placeholder id and the default matches a warm cache: the first listed thread, else the pinned Chief thread.
 */
export function resolveSelected(selectedId: string | null, loaded: boolean, pinned: ThreadDTO | null, visible: readonly ThreadDTO[]): ThreadDTO | null {
  if (!loaded) return visible.find((t) => t.threadId === selectedId) ?? null;
  if (pinned && (pinned.threadId === selectedId || selectedId === NEW_CHIEF_THREAD)) return pinned;
  return visible.find((t) => t.threadId === selectedId) ?? visible[0] ?? pinned;
}

/** §12.1.1: the lines for the states in which the Chief cannot answer right now. `ready` and `working` show none. */
export function chiefBanner(state: string | undefined, reason?: string | null): { state: ChiefBannerState; text: string; canPost: boolean } | null {
  if (state === "offline") return { state, canPost: true, text: "The Chief is offline (no machine is running it). Messages you send wait unread; it will see them when it is back." };
  if (state === "paused") return { state, canPost: false, text: `The Chief is paused${reason ? `: ${reason}` : ""}. A person can resume it.` };
  if (state === "capped") return { state, canPost: false, text: "The Chief is out of budget for today (it resets at 00:00 UTC), or raise the cap." };
  return null;
}

export type Citation = { key: string; label: string; to: string };

/**
 * The links in a Chief reply (spec §5.2: a citation per fact, the task or decision it read): `#12` (a task number),
 * `tsk_…` and `dec_…` ids. Only references that resolve become links; the order of first mention is kept.
 */
export function citationsOf(message: Pick<MessageDTO, "body" | "projectId">, tasks: readonly TaskDTO[]): Citation[] {
  const out: Citation[] = [];
  const seen = new Set<string>();
  const add = (c: Citation) => {
    if (!seen.has(c.key)) {
      seen.add(c.key);
      out.push(c);
    }
  };
  for (const m of message.body.matchAll(/#(\d{1,7})\b|\b(tsk_[a-z0-9]+)\b|\b(dec_[a-z0-9]+)\b/gi)) {
    if (m[1]) {
      const n = Number(m[1]);
      const hit = tasks.find((t) => t.number === n && t.projectId === message.projectId) ?? tasks.find((t) => t.number === n);
      if (hit) add({ key: hit.id, label: `#${hit.number}`, to: `/tasks/${hit.id}` });
    } else if (m[2]) {
      const hit = tasks.find((t) => t.id === m[2]);
      if (hit) add({ key: hit.id, label: `#${hit.number}`, to: `/tasks/${hit.id}` });
    } else if (m[3]) add({ key: m[3], label: m[3], to: `/inbox?decision=${encodeURIComponent(m[3])}` });
  }
  return out;
}
